import { Flex } from '@chakra-ui/react'
import {
  ContentNode,
  DataTypeConfig,
  ListContentNode,
  evaluateConditionGroup,
  processRuntimeConfig,
} from '@helo/cms'
import { Deferred } from '@helo/utils'
import { Fragment } from 'react/jsx-runtime'

import { TypeRenderer } from '../TypeRenderer'
import { ListContentNodeParts } from './listContentNode.parts'
import { ListLayout } from './listLayout'

export type ListRendererProps = {
  contentNode: ListContentNode
}

export type ListState = {
  listDirection: 'row' | 'column'
  listOptions?: {
    filter?: any
    config?: DataTypeConfig
    maxItemCount?: number
    disableListWrapper?: boolean
    // If the list is loading, this will be the number of items to show.
    listLoadingCount?: number
    listGapSize?: 'default' | 'wide'
    preset?: 'default' | 'supplementalDrawer'
  }
  // State to merge into each list item's state
  listItemState?: Record<string, unknown>
}

export const ListRenderer = ({
  contentNode,
  isLoading,
  disableLoadingSkeletons,
  ...props
}: ListRendererProps & {
  isLoading?: boolean
  disableLoadingSkeletons?: boolean
}) => {
  const direction = (contentNode.state?.listDirection as string) || 'row'
  const listOptions = contentNode.state?.listOptions as ListState['listOptions']
  const styles = contentNode.state?.styles
  const resolvedListOptions = ListLayout.resolveListOptions(listOptions)
  const listLoadingCount = resolvedListOptions.listLoadingCount
  const listGap = ListLayout.gapSize(resolvedListOptions.listGapSize)
  const filter = listOptions?.filter
  const maxItemCount = listOptions?.maxItemCount
  const disableListWrapper = listOptions?.disableListWrapper
  const listItemState = contentNode.state
    ?.listItemState as ListState['listItemState']
  const normalizeListItem = (item: unknown): ContentNode => {
    if (
      item &&
      typeof item === 'object' &&
      'kind' in item &&
      typeof (item as ContentNode).kind === 'string'
    ) {
      return item as ContentNode
    }

    return Deferred.merge({ data: item })(contentNode.ofType)
  }

  const resolvedItems = ListContentNodeParts.resolveListItemData(contentNode)
  const hydratedItems = (Array.isArray(resolvedItems) ? resolvedItems : []).map(
    normalizeListItem,
  )
  const limitedItems = maxItemCount
    ? hydratedItems.slice(0, maxItemCount)
    : hydratedItems

  const createLoadingItems = (count: number) =>
    Array(count)
      .fill(null)
      .map(() => Deferred.merge({ data: undefined })(contentNode.ofType))

  const filteredItems = filter
    ? limitedItems.filter(item => {
        const processedFilter = processRuntimeConfig(filter, {
          item,
        })
        return evaluateConditionGroup(processedFilter)
      })
    : limitedItems

  const skeletonRenderCount = ListContentNodeParts.getSkeletonRenderCount(
    contentNode,
    {
      isLoading,
      fallback: listLoadingCount,
      disableLoadingSkeletons,
    },
  )
  const skeletonItems = createLoadingItems(skeletonRenderCount)
  const displayItems = [...filteredItems, ...skeletonItems]
  const customStyles = styles && typeof styles === 'object' ? styles : {}

  if (displayItems.length === 0) {
    if (
      !disableLoadingSkeletons &&
      filteredItems.length > 0 &&
      !ListContentNodeParts.isConfirmedEmpty(contentNode, isLoading)
    ) {
      return (
        <Flex
          gap={listGap}
          {...props}
          alignItems={direction === 'row' ? 'center' : 'flex-start'}
          flexDirection={direction as 'row' | 'column'}
          {...customStyles}
        >
          {filteredItems.map((item, i) => (
            <Fragment key={i}>
              <TypeRenderer contentNode={item} isLoading={false} />
            </Fragment>
          ))}
        </Flex>
      )
    }

    return null
  }

  const children = displayItems.map((item, i) => {
    const isSkeletonItem = i >= filteredItems.length
    const itemWithState = listItemState
      ? {
          ...item,
          state: Deferred.merge(listItemState)(item.state ?? {}),
        }
      : item
    return (
      <Fragment key={i}>
        <TypeRenderer contentNode={itemWithState} isLoading={isSkeletonItem} />
      </Fragment>
    )
  })

  if (disableListWrapper) {
    return <>{children}</>
  }

  return (
    <Flex
      gap={listGap}
      {...props}
      alignItems={direction === 'row' ? 'center' : 'flex-start'}
      flexDirection={direction as 'row' | 'column'}
      {...customStyles}
    >
      {children}
    </Flex>
  )
}
